import { useState, useEffect } from "react";
import { useAccountant } from "@/contexts/AccountantContext";

const PRESETS = [
  { name: "Kola", desc: "Friendly and reliable" },
  { name: "Ada", desc: "Sharp and precise" },
  { name: "Amina", desc: "Calm and thorough" },
];

function capitalize(s) {
  const t = (s || "").trim();
  if (!t) return "";
  return t[0].toUpperCase() + t.slice(1).toLowerCase();
}

/**
 * Bottom-sheet modal to rename the accountant at any time.
 * Applies instantly everywhere via AccountantContext.
 */
export default function RenameAccountantModal({ onClose, onRenamed }) {
  const { name, updateSettings } = useAccountant();
  const [selectedCard, setSelectedCard] = useState(() => {
    const m = PRESETS.find((p) => p.name.toLowerCase() === (name || "").toLowerCase());
    return m ? m.name : null;
  });
  const [customName, setCustomName] = useState(() => {
    const isPreset = PRESETS.some((p) => p.name.toLowerCase() === (name || "").toLowerCase());
    return isPreset ? "" : name || "";
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { /* ensure state seed runs only once */ }, []);

  const effectiveName = customName.trim() || selectedCard || "";
  const isValid = effectiveName.length >= 2 && effectiveName.length <= 20;

  const pickCard = (n) => {
    setSelectedCard(n);
    setCustomName("");
  };

  const onCustomChange = (v) => {
    if (/[^a-zA-Z\s'-]/.test(v)) return;
    if (v.length > 20) return;
    setCustomName(v);
    if (v) setSelectedCard(null);
  };

  const save = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      const final = capitalize(effectiveName);
      await updateSettings({ accountant_name: final });
      onRenamed?.(final);
      onClose();
    } catch (e) {
      alert("Rename failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/45" onClick={onClose}>
      <div
        className="w-full max-w-[430px] bg-white rounded-t-[24px] p-5 pb-6"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "slideUp 0.3s ease-out" }}
        data-testid="rename-modal"
      >
        <div className="w-12 h-1.5 bg-[var(--br2)] rounded-full mx-auto mb-4" />
        <div className="text-[18px] font-black text-[var(--tx)] mb-1">Rename your accountant</div>
        <div className="text-[13px] text-[var(--tx2)] mb-5 leading-snug">
          Choose a new name or type your own. The change applies everywhere in the app immediately.
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          {PRESETS.map((p) => {
            const active = selectedCard === p.name;
            return (
              <button
                key={p.name}
                onClick={() => pickCard(p.name)}
                data-testid={`rename-card-${p.name.toLowerCase()}`}
                className="rounded-2xl p-3 text-center transition-all"
                style={{
                  background: active ? "var(--ac)" : "white",
                  border: active ? "1.5px solid var(--ac)" : "1.5px solid var(--br)",
                  boxShadow: active ? "0 4px 14px rgba(13,110,110,0.22)" : "0 2px 8px rgba(28,25,23,0.04)",
                }}
              >
                <div className="text-[15px] font-extrabold mb-1" style={{ color: active ? "#fff" : "var(--tx)" }}>
                  {p.name}
                </div>
                <div className="text-[10px] leading-tight" style={{ color: active ? "rgba(255,255,255,0.85)" : "var(--tx3)" }}>
                  {p.desc}
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1 h-px bg-[var(--br)]" />
          <div className="text-[10px] text-[var(--tx3)] font-semibold uppercase tracking-wider">
            — or choose your own name —
          </div>
          <div className="flex-1 h-px bg-[var(--br)]" />
        </div>

        <input
          className="w-full bg-[var(--sf2)] border-[1.5px] border-[var(--br)] rounded-2xl px-4 py-3 text-[15px] outline-none focus:border-[var(--ac)] font-sans mb-4"
          value={customName}
          onChange={(e) => onCustomChange(e.target.value)}
          placeholder="Type any name you like..."
          maxLength={20}
          data-testid="rename-input"
        />

        <button
          onClick={save}
          disabled={!isValid || saving}
          data-testid="rename-save"
          className="w-full rounded-2xl py-3 font-extrabold text-[14px] mb-2 transition-all"
          style={{
            background: isValid ? "var(--ac)" : "var(--br2)",
            color: isValid ? "white" : "var(--tx3)",
          }}
        >
          {saving ? "Saving…" : "Save Name"}
        </button>
        <button
          onClick={onClose}
          className="w-full bg-[var(--sf2)] border border-[var(--br)] text-[var(--tx2)] rounded-2xl py-3 font-bold text-[13px]"
        >
          Cancel
        </button>

        <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
      </div>
    </div>
  );
}
